import { useState, useRef, useEffect } from "react";
import API from "@/lib/api";
import { openCamera, openPhotoPicker } from "@/lib/camera";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Camera, Mic, MicOff, Check, Pencil, Trash2, Loader2, ImageIcon, X, ArrowRight, AlertCircle
} from "lucide-react";
import { toast } from "sonner";
import { useAI } from "@/lib/ai";
import { AIResultNote } from "@/components/AIResultNote";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const MIN_PHOTOS = 5;
const MAX_PHOTOS = 500;

function VoiceInput({ onTranscript, disabled }) {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const startListening = () => {
    if (!("webkitSpeechRecognition" in window || "SpeechRecognition" in window)) {
      toast.error("Voice input not supported in this browser");
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      onTranscript(transcript);
    };

    recognition.onerror = () => {
      setListening(false);
    };

    recognition.onend = () => {
      setListening(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setListening(false);
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={listening ? stopListening : startListening}
      disabled={disabled}
      className={`text-xs ${listening ? "bg-red-50 border-red-300 text-red-700" : ""}`}
      data-testid="voice-input-button"
    >
      {listening ? (
        <><MicOff className="w-3.5 h-3.5 mr-1" /> Stop</>
      ) : (
        <><Mic className="w-3.5 h-3.5 mr-1" /> Voice</>
      )}
    </Button>
  );
}

function PhotoCard({ photo, onApprove, onOverride, onDelete }) {
  const [mode, setMode] = useState("view"); // view, override
  const [overrideText, setOverrideText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await onApprove(photo.id);
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverride = async () => {
    if (!overrideText.trim()) { toast.error("Enter a description"); return; }
    setSubmitting(true);
    try {
      await onOverride(photo.id, overrideText.trim());
      setMode("view");
      setOverrideText("");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border border-zinc-200 overflow-hidden" data-testid={`photo-card-${photo.id}`}>
      <div className="relative">
        <img
          src={`${API_BASE}/api/photos/${photo.id}/image`}
          alt="Job photo"
          className="w-full h-40 object-cover bg-zinc-100"
          loading="lazy"
        />
        <button
          onClick={() => onDelete(photo.id)}
          className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70 transition-colors"
          data-testid={`delete-photo-${photo.id}`}
        >
          <X className="w-3.5 h-3.5 text-white" />
        </button>
        {photo.is_approved && (
          <Badge className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px]">
            <Check className="w-3 h-3 mr-0.5" /> Approved
          </Badge>
        )}
      </div>
      <CardContent className="p-3 space-y-2">
        <div>
          <p className="text-[10px] text-zinc-400 uppercase tracking-wider font-bold mb-0.5">
            {photo.description_source === "ai" ? "AI Description" :
             photo.description_source === "ai_approved" ? "AI Description (Approved)" :
             "Your Description"}
          </p>
          <p className="text-xs text-zinc-700 leading-relaxed">
            {photo.final_description || photo.ai_description}
          </p>
          {photo.description_source?.startsWith("ai") && (
            <AIResultNote feature="photo" refId={photo.id} text={photo.final_description || photo.ai_description} />
          )}
        </div>

        {!photo.is_approved && mode === "view" && (
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              onClick={handleApprove}
              disabled={submitting || !(photo.final_description || photo.ai_description)}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-8"
              data-testid={`approve-desc-${photo.id}`}
            >
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" /> Approve</>}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => { setMode("override"); setOverrideText(photo.ai_description || ""); }}
              className="flex-1 text-xs h-8"
              data-testid={`override-desc-${photo.id}`}
            >
              <Pencil className="w-3 h-3 mr-1" /> Edit
            </Button>
          </div>
        )}

        {!photo.is_approved && mode === "override" && (
          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between">
              <p className="text-[10px] text-zinc-500 font-bold">Override Description</p>
              <VoiceInput
                onTranscript={(text) => setOverrideText(text)}
                disabled={submitting}
              />
            </div>
            <Textarea
              value={overrideText}
              onChange={e => setOverrideText(e.target.value)}
              rows={3}
              className="text-xs"
              placeholder="Type or use voice to describe what's in the photo..."
              data-testid={`override-text-${photo.id}`}
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleOverride}
                disabled={submitting || !overrideText.trim()}
                className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white text-xs h-8"
                data-testid={`save-override-${photo.id}`}
              >
                {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setMode("view"); setOverrideText(""); }}
                className="text-xs h-8"
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function JobPhotos({ requestId, onContinue }) {
  const { aiOn } = useAI();
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const photoCount = Array.isArray(photos) ? photos.length : 0;
  const atMax = photoCount >= MAX_PHOTOS;
  const meetsMin = photoCount >= MIN_PHOTOS;
  const remainingToMin = Math.max(0, MIN_PHOTOS - photoCount);

  const fetchPhotos = async () => {
    try {
      const { data } = await API.get(`/photos/job/${requestId}`);
      const list = Array.isArray(data?.photos) ? data.photos : [];
      setPhotos(list);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (!requestId) return;
    fetchPhotos();
    // Poll every 5 seconds so the counter stays current even if AI analysis is slow.
    // Pause polling while the tab is hidden to save battery and API calls.
    let interval = setInterval(fetchPhotos, 5000);
    const onVisibility = () => {
      clearInterval(interval);
      if (document.visibilityState === "visible") {
        fetchPhotos();
        interval = setInterval(fetchPhotos, 5000);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [requestId]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (photoCount >= MAX_PHOTOS) {
      toast.error(`Maximum of ${MAX_PHOTOS} photos reached for this job.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("Photo is too large (max 10MB). Please retake at lower quality.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
      return;
    }

    setUploading(true);
    setAnalyzing(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("request_id", requestId);

    try {
      await API.post("/photos/analyze", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      toast.success("Photo uploaded");
      fetchPhotos();
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Failed to upload photo";
      toast.error(msg);
      console.error("Photo upload failed:", err);
      // Even on error, refresh — backend may have saved the photo with AI fallback
      fetchPhotos();
    } finally {
      setUploading(false);
      setAnalyzing(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (cameraInputRef.current) cameraInputRef.current.value = "";
    }
  };

  const handleApprove = async (photoId) => {
    try {
      await API.post(`/photos/${photoId}/approve`);
      toast.success("Description approved");
      fetchPhotos();
    } catch (err) {
      toast.error("Failed to approve");
    }
  };

  const handleOverride = async (photoId, description) => {
    try {
      await API.post(`/photos/${photoId}/override`, { description });
      toast.success("Description updated");
      fetchPhotos();
    } catch (err) {
      toast.error("Failed to update description");
    }
  };

  const handleDelete = async (photoId) => {
    try {
      await API.delete(`/photos/${photoId}`);
      toast.success("Photo deleted");
      fetchPhotos();
    } catch (err) {
      toast.error("Failed to delete photo");
    }
  };

  return (
    <div className="space-y-3" data-testid="job-photos-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4 text-zinc-400" />
          <h4 className="text-sm font-heading font-semibold text-zinc-900">
            Job Photos ({photoCount})
          </h4>
        </div>
      </div>

      {/* Photo counter (always visible) */}
      <div
        className="flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2"
        data-testid="photo-counter"
      >
        <span className="text-xs font-medium text-zinc-700">
          {photoCount} photo{photoCount === 1 ? "" : "s"} uploaded
          <span className="ml-1 text-[10px] text-zinc-500">
            ({MIN_PHOTOS} minimum / {MAX_PHOTOS} maximum)
          </span>
        </span>
        {meetsMin ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">
            <Check className="w-3 h-3 mr-0.5" /> Minimum met
          </Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]">
            {remainingToMin} more required
          </Badge>
        )}
      </div>

      {/* At-max message */}
      {atMax && (
        <div
          className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2"
          data-testid="photo-max-reached-message"
        >
          <AlertCircle className="w-3.5 h-3.5 text-rose-600 shrink-0" />
          <p className="text-xs text-rose-700">
            Maximum of {MAX_PHOTOS} photos reached for this job.
          </p>
        </div>
      )}

      {/* Below-minimum message */}
      {!meetsMin && (
        <div
          className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2"
          data-testid="photo-min-required-message"
        >
          <AlertCircle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
          <p className="text-xs text-amber-800">
            Please upload at least {MIN_PHOTOS} photos to continue. You have uploaded {photoCount} of {MIN_PHOTOS} required.
          </p>
        </div>
      )}

      {/* Upload buttons */}
      <div className="flex gap-2">
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={handleFileUpload}
          data-testid="camera-input"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,.heic,.heif"
          className="hidden"
          onChange={handleFileUpload}
          data-testid="file-input"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => openCamera(cameraInputRef)}
          disabled={uploading || atMax}
          className="flex-1 text-xs h-9"
          data-testid="take-photo-button"
        >
          {uploading ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> {analyzing && aiOn ? "AI Analyzing..." : "Uploading..."}</>
          ) : (
            <><Camera className="w-3.5 h-3.5 mr-1.5" /> Take Photo</>
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => openPhotoPicker(fileInputRef)}
          disabled={uploading || atMax}
          className="flex-1 text-xs h-9"
          data-testid="upload-photo-button"
        >
          <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Upload
        </Button>
      </div>

      {/* Photo grid */}
      {photoCount > 0 && (
        <div className="grid grid-cols-1 gap-3">
          {photos.map(photo => (
            <PhotoCard
              key={photo.id}
              photo={photo}
              onApprove={handleApprove}
              onOverride={handleOverride}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {photoCount === 0 && !uploading && (
        <div className="text-center py-4 border border-dashed border-zinc-200 rounded-xl">
          <Camera className="w-6 h-6 text-zinc-300 mx-auto mb-1.5" />
          <p className="text-xs text-zinc-400">No photos yet. Take or upload a photo to get started.</p>
          {aiOn && <p className="text-[10px] text-zinc-400 mt-0.5">AI will automatically describe each photo</p>}
        </div>
      )}

      {/* Continue to Next Section button */}
      <Button
        size="sm"
        onClick={() => {
          if (!meetsMin) return;
          if (typeof onContinue === "function") {
            onContinue();
          } else {
            toast.success("Minimum photos uploaded. Ready for the next section.");
          }
        }}
        disabled={!meetsMin}
        className="w-full h-10 text-sm bg-zinc-900 hover:bg-zinc-800 text-white disabled:bg-zinc-200 disabled:text-zinc-400"
        data-testid="continue-to-next-section-button"
      >
        Continue to Next Section <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
      </Button>
    </div>
  );
}
